<template>
  <v-dialog
      v-model="dialog"
      :width="dialogWidth"
      persistent
      @keydown.enter.prevent="save"
  >
    <v-card
        class="resizable-card"
        :class="{ 'no-select': resizing }"
        :style="{ height: dialogHeight + 'px' }"
    >
      <v-card-title/>
      <v-card-text class="resizable-card__body">
        <v-row dense>
          <v-col cols="12">
            <v-autocomplete
                v-model="model"
                :items="items"
                item-text="key"
                item-value="key"
                label="Select Currency Kind(s):"
                return-object
                multiple
                small-chips
                deletable-chips
                hide-details
            />
          </v-col>
        </v-row>

        <v-row v-if="forms.length" dense>
          <v-col cols="12">
            <v-simple-table
                dense
                style="max-height: 300px; margin-top: 16px; overflow: auto;"
            >
              <thead>
              <tr>
                <th class="text-left">Currency</th>
                <th class="text-left" style="width: 120px;">Percentage</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="f in forms" :key="f.kind.key">
                <td>{{ f.kind.key }}</td>
                <td>
                  <v-text-field
                      v-model.number="f.percentage"
                      type="number"
                      min="1"
                      max="100"
                      dense
                      hide-details
                      @keydown.enter.stop
                      style="margin-top: -4px"
                  />
                </td>
              </tr>
              </tbody>
            </v-simple-table>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="resizable-card__actions">
        <v-spacer/>
        <v-btn color="blue darken-1" text @click="cancel">Cancel</v-btn>
        <v-btn color="blue darken-1" text @click="save">Save</v-btn>
      </v-card-actions>

      <div
          class="resize-handle"
          @mousedown.stop.prevent="startResize"
      ></div>
    </v-card>
  </v-dialog>
</template>

<script>
import { ViewRules } from "@/common/ViewRules";
import { RedactorUtils } from "@/modules/redactor/core/RedactorUtils";
import { CurrencyCorrection } from "@/modules/redactor/models/currency/CurrencyCorrection";

export default {
  name: "r-currency-correction-dialog",

  data: () => ({
    dialog: false,
    resolve: null,
    reject: null,
    model: [],
    forms: [],
    rules: ViewRules.READONLY,
    items: [],
    prefillByKey: null,
    dialogWidth: 690,
    dialogHeight: 546,
    resizing: false,
    resizeStartX: 0,
    resizeStartY: 0,
    resizeStartWidth: 690,
    resizeStartHeight: 546,
    viewportPadding: 32
  }),

  computed: {
    empty() {
      return this.forms.length === 0;
    }
  },

  watch: {
    model: {
      handler() {
        this.syncForms();
      }
    }
  },

  methods: {
    show(rules, input) {
      this.dialog = true;
      this.rules = rules;
      const src = Array.isArray(input) ? input : (input ? [input] : []);
      this.prefillByKey = new Map();

      src.forEach(cur => {
        const key = cur?.kind?.key;
        if (key) this.prefillByKey.set(key, cur);
      });

      this.model = this.toKindArray(src);

      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },

    loadItems() {
      const mapKind = RedactorUtils.getCurrentMapKind();
      const mapId = mapKind?.code ?? null;
      const all = RedactorUtils.getCurrencyKinds() || [];

      this.items = (mapId && mapId !== 9999)
          ? all.filter(i => i?.maps?.includes(mapId))
          : all;
    },

    toKindArray(input) {
      const arr = Array.isArray(input) ? input : (input ? [input] : []);
      const byKey = new Map(this.items.map(k => [k.key, k]));

      return arr
          .map(c => byKey.get(c?.kind?.key))
          .filter(Boolean);
    },

    syncForms() {
      const existing = new Map(this.forms.map(c => [c.kind.key, c]));
      this.forms = this.model.map(kind => {
        const cur = existing.get(kind.key);
        if (cur) {
          cur.kind = kind;
          return cur;
        }

        return this.makeCurrency(kind);
      });
    },

    makeCurrency(kind) {
      const c = new CurrencyCorrection();
      c.kind = kind;
      const pre = this.prefillByKey?.get(kind.key);
      c.percentage = typeof pre?.percentage === "number" ? pre.percentage : 1;

      return c;
    },

    save() {
      const list = Array.isArray(this.forms) ? this.forms : [];
      const valid =
          list.length > 0 &&
          list.every(i => i && typeof i.valid === "function" && i.valid());

      if (!valid) {
        this.$alert("Invalid config!");
        return;
      }

      this.resolve && this.resolve(list.map(i => (i.clone ? i.clone() : i)));
      this.discard();
    },

    cancel() {
      this.reject && this.reject();
      this.discard();
    },

    discard() {
      this.dialog = false;
      this.model = [];
      this.forms = [];
      this.prefillByKey = null;
      this.stopResize();
    },

    startResize(e) {
      this.resizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      this.resizeStartWidth = this.dialogWidth;
      this.resizeStartHeight = this.dialogHeight;
      window.addEventListener("mousemove", this.onResize);
      window.addEventListener("mouseup", this.stopResize);
    },

    onResize(e) {
      if (!this.resizing) return;
      const dx = e.clientX - this.resizeStartX;
      const dy = e.clientY - this.resizeStartY;

      let newWidth = this.resizeStartWidth + dx;
      let newHeight = this.resizeStartHeight + dy;

      const minWidth = 500;
      const minHeight = 340;

      if (newWidth < minWidth) newWidth = minWidth;
      if (newHeight < minHeight) newHeight = minHeight;

      this.dialogWidth = newWidth;
      this.dialogHeight = newHeight;
    },

    stopResize() {
      if (!this.resizing) return;
      this.resizing = false;
      window.removeEventListener("mousemove", this.onResize);
      window.removeEventListener("mouseup", this.stopResize);
    }
  },

  created() {
    this.loadItems();
  },

  beforeDestroy() {
    window.removeEventListener("mousemove", this.onResize);
    window.removeEventListener("mouseup", this.stopResize);
  }
};
</script>

<style scoped>
.resizable-card {
  position: relative;
  display: flex;
  flex-direction: column;
}
.resizable-card__body {
  flex: 1;
  overflow: auto;
}
.resizable-card__actions {
  flex-shrink: 0;
}
.resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: se-resize;
  border-right: 2px solid rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid rgba(0, 0, 0, 0.3);
}
.no-select {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
</style>